// Call alerts: the browser notification for an incoming call, the after-login
// card that asks for it, and the flashing tab title. Pure, unit-tested; the
// Phone and PhoneApp do the browser calls.

/** The browser's notification permission for this site ('unsupported': no Notification API). */
export type AlertPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * What the app shows about alerts and sound:
 * - card:    after login, once per browser: "Get alerts for incoming calls? Turn on / Not now"
 * - line:    after "Not now": a one-line reminder with Turn on
 * - blocked: the person blocked notifications: "Call alerts are blocked · How to fix"
 * - sound:   alerts are settled, but the page may not play the ringtone until a click
 * - none:    nothing to show
 */
export type AlertPrompt = 'card' | 'line' | 'blocked' | 'sound' | 'none';

export interface AlertPromptInput {
  permission: AlertPermission;
  /** "Not now" was chosen on this browser */
  notNow: boolean;
  /** the blocked banner was hidden on this browser (Settings still shows the steps) */
  blockedHidden: boolean;
  /** the ringtone can play */
  soundOn: boolean;
}

export function alertPrompt(i: AlertPromptInput): AlertPrompt {
  if (i.permission === 'default') return i.notNow ? 'line' : 'card';
  if (i.permission === 'denied' && !i.blockedHidden) return 'blocked';
  return i.soundOn ? 'none' : 'sound';
}

/** The notification for a ringing call. `to` = which of the user's numbers was called (only with 2+ numbers). */
export function callAlert(who: string, to: string | null): { title: string; body: string } {
  return { title: 'Incoming call', body: to ? `${who}\nto ${to}` : who };
}

/** Tab title while a call rings: alternates every second so the tab stands out. */
export function ringingTitle(tick: number, who: string): string {
  return tick % 2 === 0 ? '📞 Incoming call…' : `${who} · CloudPhone`;
}

/** What Settings says about call alerts on this browser. */
export function alertStatus(p: AlertPermission): string {
  switch (p) {
    case 'granted':
      return 'On. Incoming calls show an alert when CloudPhone isn’t the tab in front.';
    case 'denied':
      return 'Blocked in this browser, so you won’t see calls while CloudPhone is in the background.';
    case 'default':
      return 'Off. Turn on to see incoming calls while CloudPhone is in the background.';
    default:
      return 'This browser can’t show call alerts. Keep CloudPhone open in front, or use a current Chrome, Edge, Firefox or Safari.';
  }
}

/** One alert per call, each with its own tag (see Phone.alertIfRinging). */
export function alertTag(callId: number): string {
  return `cloudphone-call-${callId}`;
}

/** Notifications with buttons need a service worker and a browser that shows actions (Chrome, Edge, current Firefox; not Safari or older Firefox: checked by maxActions, not by name). */
export function canShowAlertButtons(maxActions: number | undefined, workerReady: boolean): boolean {
  return workerReady && typeof maxActions === 'number' && maxActions >= 2;
}

/** What a click in the alert asks for (from the service worker). Unknown → just bring the tab forward. */
export type AlertAction = 'answer' | 'decline' | 'open';

export function alertAction(v: unknown): AlertAction {
  return v === 'answer' || v === 'decline' ? v : 'open';
}
